/**
 * A read that rides out a transient failure, the way `readProfileWithRetries` does for the profile:
 * three attempts with a widening pause, and a 4xx is never retried, because a 4xx is an answer.
 * Only a 5xx or a thrown network error counts as "did not answer", and after the last attempt the
 * result is `'unavailable'` — distinct from a refusal, so a screen can say the true thing.
 */
export async function readWithRetries<T extends { response: Response }>(
  read: () => Promise<T>,
  attemptsLeft = 2,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T | 'unavailable'> {
  try {
    const result = await read();
    const status = result.response.status;
    if (result.response.ok || (status >= 400 && status < 500)) {
      return result;
    }
  } catch {
    // Network-level failure: falls through to the retry decision, same as a 5xx.
  }
  if (attemptsLeft <= 0) {
    return 'unavailable';
  }
  await wait((2 - attemptsLeft) * 200 + 200);
  return readWithRetries(read, attemptsLeft - 1, wait);
}
